import React, { useState } from 'react';
import PromptConfirmationModal from './PromptConfirmationModal';
import { usePrompts } from '@/hooks/usePrompts';

interface TransformedPrompt {
  prompt: string;
  wordCount: string;
  status: string;
  supplement_id: number;
  prompt_id: number;
  category: string;
}

interface GenericItem {
  id: number;
  name: string;
  status: string;
  key: string;
  minPrompts?: number;
  maxPrompts?: number;
}

interface SupplementsSectionProps {
  items: GenericItem[];
  title: string;
  type: string; // 'SUPPLEMENT' or 'APPLICATION_TYPE'
  onSelectPrompt: (prompt: string, wordCount: string, type: string, promptId?: number) => void;
}

const SupplementsSection: React.FC<SupplementsSectionProps> = ({ 
  items,
  title,
  type,
  onSelectPrompt
}) => {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedPrompt, setSelectedPrompt] = useState<{prompt: string, wordCount: string, type: string, promptId?: number} | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  // Get the active item ID for prompts
  const activeItem = items.find(item => item.key === activeKey);
  const activeItemId = activeItem?.id;

  // Simple prompts hook - only fetches for the currently active item
  const { data: prompts, isLoading, error } = usePrompts(type, activeItemId);

  const handleChange = (key: string, itemId: number) => {
    setActiveKey(activeKey === key ? null : key);
    setCurrentSlide(0);
    // No need for manual request - the hook will automatically fetch when activeItemId changes
  };

  const handlePromptClick = (prompt: string, wordCount: string, promptType: string, promptId?: number) => {
    setSelectedPrompt({ prompt, wordCount, type, promptId });
    setIsModalVisible(true);
  };

  const handleConfirmPrompt = () => {
    if (selectedPrompt) {
      onSelectPrompt(selectedPrompt.prompt, selectedPrompt.wordCount, selectedPrompt.type, selectedPrompt.promptId);
      setIsModalVisible(false);
      setSelectedPrompt(null);
    }
  };

  const handleCancelPrompt = () => {
    setIsModalVisible(false);
    setSelectedPrompt(null);
  };

  const nextSlide = (totalSlides: number) => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = (totalSlides: number) => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };



  return (
    <div className="prompts-section">
      <h5 className="text-base font-medium mb-3" style={{ color: 'var(--text-primary)' }}>{title}</h5>
      {
        items.length === 0 && (
          <div className="text-center py-8">
            {type === 'SUPPLEMENT' ? (
              <div className="flex flex-col items-center space-y-3">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'var(--surface-hover)' }}>
                  <svg className="w-8 h-8" style={{ color: 'var(--text-tertiary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z" />
                  </svg>
                </div>
                <div className="text-sm max-w-xs" style={{ color: 'var(--text-secondary)' }}>
                  No data available for this university. Click here to submit a request for updated details.
                </div>
                <button className="mt-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm hover:bg-blue-100 transition-colors">
                  Request Data
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center space-y-3">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'var(--surface-hover)' }}>
                  <svg className="w-8 h-8" style={{ color: 'var(--text-tertiary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Please select an application type to view available prompts
                </div>
              </div>
            )}
          </div>
        )
      }
      
      {items.map((item, index: number) => {
        const transformedPrompts: TransformedPrompt[] | undefined = prompts?.map(prompt => ({
          prompt: prompt.prompt_text,
          wordCount: prompt.word_count.toString(),
          status: item.status,
          supplement_id: item.id,
          prompt_id: prompt.id,
          category: prompt.primary_category_name
        }));
        
        return (
          <div key={item.key} className="border rounded-lg mb-4" style={{ borderColor: 'var(--border-primary)' }}>
            <div
              className="p-4 cursor-pointer hover:bg-[var(--surface-hover)] transition-colors flex justify-between items-center flex-wrap"
              onClick={() => handleChange(item.key, item.id)}
            >
              <span className="font-medium">{item.name}</span>
              <div className="flex items-center space-x-2">
                {
                  type === 'SUPPLEMENT' && (
                    <span className="px-2 py-1 rounded-full text-xs"
                      style={item.status.toLocaleLowerCase() === 'mandatory' || item.status.toLocaleLowerCase() === 'required'
                        ? { background: 'rgba(239, 68, 68, 0.12)', color: 'var(--danger-dark)' }
                        : { background: 'rgba(34, 197, 94, 0.12)', color: 'var(--success-light)' }
                    }>
                      {item.status?.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                    </span>
                  )
                }
              
                
                <svg 
                  className={`w-5 h-5 transition-transform ${activeKey === item.key ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            {activeKey === item.key && (
              <div className="border-t p-4" style={{ borderColor: 'var(--border-primary)' }}>
                {/* Header Section */}
                {
                  type === 'SUPPLEMENT' && (
                    <div className="mb-6">
                      <div className="flex items-center space-x-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ background: 'rgba(59, 130, 246, 0.12)', color: 'var(--info-dark)' }}>
                          {item.minPrompts || 0}-{item.maxPrompts || item.minPrompts || 0}/{transformedPrompts?.length || 0} Required
                        </span>
                      </div>
                    </div>)
                }
               
                {isLoading ? (
                  <div className="text-center py-4">Loading prompts...</div>
                ) : error ? (
                  <div className="text-center py-4 text-red-600">Error loading prompts</div>
                ) : !transformedPrompts || transformedPrompts.length === 0 ? (
                  <div className="text-center py-4" style={{ color: 'var(--text-secondary)' }}>No prompts available</div>
                ) : transformedPrompts.length > 1 ? (
                  <div className="relative">
                    <div className="overflow-hidden">
                      <div 
                        className="flex transition-transform duration-300"
                        style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                      >
                        {transformedPrompts.map((prompt: TransformedPrompt, idx: number) => (
                          <div 
                            key={idx} 
                            className="carousel-slide w-full flex-shrink-0 cursor-pointer"
                            onClick={() => handlePromptClick(prompt.prompt, prompt.wordCount, type, prompt.prompt_id)}
                          >
                            <div className="rounded-lg p-4 mb-4" style={{ background: 'var(--surface-hover)' }}>
                              <div className="mb-2">
                                <span className="font-medium">Category: </span>
                                <span className='px-2 py-1 rounded-full text-xs' style={{ background: 'rgba(34, 197, 94, 0.12)', color: 'var(--success-light)' }}>
                                  {prompt?.category?.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
                                </span>
                              </div>
                              <div className="mb-2">
                                <span className="font-medium">Word Count: </span>
                                <span className="px-2 py-1 rounded-full text-xs" style={{ background: 'rgba(59, 130, 246, 0.12)', color: 'var(--info-dark)' }}>
                                  {prompt?.wordCount} words
                                </span>
                              </div>
                              <div>
                                <span className="font-medium">Prompt: </span>
                                <p className="mt-1" style={{ color: 'var(--text-secondary)' }}>{prompt?.prompt}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => prevSlide(transformedPrompts.length)}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 border rounded-full p-2 hover:bg-[var(--surface-hover)]"
                      style={{ background: 'var(--surface-primary)', borderColor: 'var(--border-primary)' }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => nextSlide(transformedPrompts.length)}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 border rounded-full p-2 hover:bg-[var(--surface-hover)]"
                      style={{ background: 'var(--surface-primary)', borderColor: 'var(--border-primary)' }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <div className="flex justify-center mt-4 space-x-2">
                      {transformedPrompts.map((_: TransformedPrompt, idx: number) => (
                        <button
                          key={idx}
                          className={`w-2 h-2 rounded-full ${
                            currentSlide === idx ? 'bg-blue-600' : 'bg-[var(--border-primary)]'
                          }`}
                          onClick={() => setCurrentSlide(idx)}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div
                    className="rounded-lg p-4 mb-4 cursor-pointer hover:bg-[var(--surface-hover)] transition-colors"
                    style={{ background: 'var(--surface-hover)' }}
                    onClick={() => handlePromptClick(transformedPrompts[0].prompt, transformedPrompts[0].wordCount, type, transformedPrompts[0].prompt_id)}
                  >
                    <div className="mb-2">
                      <span className="font-medium">Category: </span>
                      <span className='px-2 py-1 rounded-full text-xs' style={{ background: 'rgba(34, 197, 94, 0.12)', color: 'var(--success-light)' }}>
                        {transformedPrompts[0]?.category}
                       </span>
                    </div>
                    <div className="mb-2">
                      <span className="font-medium">Word Count: </span>
                      <span className="px-2 py-1 rounded-full text-xs" style={{ background: 'rgba(59, 130, 246, 0.12)', color: 'var(--info-dark)' }}>
                        {transformedPrompts[0]?.wordCount} words
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Prompt: </span>
                      <p className="mt-1" style={{ color: 'var(--text-secondary)' }}>{transformedPrompts[0]?.prompt}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      <PromptConfirmationModal
        isVisible={isModalVisible}
        selectedPrompt={selectedPrompt}
        onConfirm={handleConfirmPrompt}
        onCancel={handleCancelPrompt}
        title="Confirm Prompt Selection"
      />
    </div>
  );
};

export default SupplementsSection;
